import React, { useEffect, useState } from "react";
import SettingsLayout from "@/components/SettingsLayout";
import { useLocale } from "@/contexts/LocaleContext";
import api, { formatApiError } from "@/lib/api";
import { ScrollText, Filter, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AuditLog() {
  // Phase B2 — locale-aware timestamps.
  const { formatDateTime } = useLocale();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async (action = "") => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (action) params.action = action;
      const { data } = await api.get("/orgs/me/audit", { params });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Phase B5 — friendly summary for bulk-delete rows.
  const friendlySummary = (r) => {
    if (r.action !== "data.bulk_delete") return null;
    const md = r.metadata || {};
    const total = md.total_deleted ?? "?";
    const colls = Array.isArray(md.collections) ? md.collections.length : "?";
    const tagBit = md.source_tag ? ` (by source: ${md.source_tag})` : "";
    const fullBit = md.full_org_wipe ? " · full-org wipe" : "";
    return `Cleared ${total} doc(s) across ${colls} collection(s)${tagBit}${fullBit}.`;
  };

  return (
    <SettingsLayout>
      <div className="eb-overline mb-2">Activity</div>
      <h1 className="text-3xl font-semibold mb-1" data-testid="audit-heading">Audit log</h1>
      <p className="text-sm text-navy-500 mb-6">Every state-changing action in your org.</p>

      <div className="eb-card p-4 flex items-center gap-3 mb-4 flex-wrap">
        <Filter className="w-4 h-4 text-navy-500" />
        <input
          placeholder="Filter by action (e.g. user.login)"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="eb-input flex-1 max-w-md"
          data-testid="audit-filter-input"
        />
        <button className="btn-outline" onClick={() => load(filter)} data-testid="audit-filter-btn">Apply</button>
        {/* Phase B5 — one-click chip for Clear Test Data executions. */}
        <button
          type="button"
          onClick={() => { setFilter("data.bulk_delete"); load("data.bulk_delete"); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
          data-testid="audit-filter-chip-bulk-delete"
        >
          <Trash2 className="w-3 h-3" /> Bulk deletions
        </button>
      </div>

      <div className="eb-card overflow-hidden">
        {loading ? (
          <p className="p-6 text-navy-500">Loading…</p>
        ) : (
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="bg-surface text-xs uppercase tracking-wider text-navy-500">
              <tr>
                <th className="text-left px-5 py-3">When</th>
                <th className="text-left px-5 py-3">Action</th>
                <th className="text-left px-5 py-3">Target</th>
                <th className="text-left px-5 py-3">User</th>
                <th className="text-left px-5 py-3">Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const summary = friendlySummary(r);
                return (
                  <tr key={r.id} className="border-t border-navy-100 hover:bg-surface/50" data-testid={`audit-row-${r.id}`}>
                    <td className="px-5 py-3 text-navy-600 font-mono text-xs">{r.at ? formatDateTime(r.at) : ""}</td>
                    <td className="px-5 py-3 font-medium text-navy-900 inline-flex items-center gap-1.5">
                      {r.action === "data.bulk_delete" && <Trash2 className="w-3.5 h-3.5 text-destructive" />}
                      {r.action}
                    </td>
                    <td className="px-5 py-3 text-navy-600 text-xs">{r.target_type}{r.target_id ? ` · ${r.target_id.slice(0, 8)}…` : ""}</td>
                    <td className="px-5 py-3 text-navy-600 font-mono text-xs">{r.user_id ? r.user_id.slice(0, 8) : "—"}</td>
                    <td className="px-5 py-3 text-xs text-navy-500 max-w-xs">
                      {summary ? (
                        <span className="text-navy-700 dark:text-navy-100" data-testid={`audit-summary-${r.id}`}>{summary}</span>
                      ) : (
                        <span className="truncate inline-block max-w-full">{Object.keys(r.metadata || {}).length ? JSON.stringify(r.metadata) : "—"}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan="5" className="px-5 py-10 text-center text-navy-500">
                  <ScrollText className="w-8 h-8 mx-auto text-navy-300 mb-2" />
                  No entries match.
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </SettingsLayout>
  );
}
